import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import SubCategoryAttributesComponent from "./component";

const SubCategoryAttributesContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subCategoryId } = useParams();
  
  const categoryData = location.state?.categoryData;
  const subCategoryData = location.state?.subCategoryData;
  const categoryName = categoryData?.name || location.state?.categoryName || "Category";
  const subCategoryName = subCategoryData?.name || location.state?.subCategoryName || "Sub Category";
  const subCategoryIdValue = subCategoryId || subCategoryData?.id;

  const [attributeHeadings, setAttributeHeadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAttributes = () => {
    if (!subCategoryIdValue) {
      setLoading(false);
      return;
    }

    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/attributes?ownerType=SUBCATEGORY&ownerId=${subCategoryIdValue}`,
      "GET",
      undefined,
      (response) => {
        let headingsData = [];
        if (response && Array.isArray(response)) {
          headingsData = response;
        } else if (response?.attributeHeadings && Array.isArray(response.attributeHeadings)) {
          headingsData = response.attributeHeadings;
        }
        setAttributeHeadings(headingsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching attributes:", err);
        setLoading(false);
        toastr.error("Failed", "Unable to fetch attributes");
      }
    );
  };

  useEffect(() => { 
    fetchAttributes(); 
  }, [subCategoryIdValue]);

  // ✅ Add new attribute - with unitValues support
  const handleAddAttribute = (heading, attributeData, onSuccess, onError) => {
    setIsSubmitting(true);
    
    const newAttribute = {
      key: attributeData.key,
      dataType: attributeData.dataType,
      isRequired: attributeData.isRequired === "Yes",
    };
    
    // ✅ Add isBoolean for BOOLEAN type
    if (attributeData.dataType === "BOOLEAN" && attributeData.isBoolean) {
      newAttribute.isBoolean = attributeData.isBoolean; // "Yes" or "No"
    }
    
    // ✅ Handle Unit for DROPDOWN type
    if (attributeData.dataType === "DROPDOWN") {
      if (attributeData.unitType === "Yes") {
        newAttribute.unit = "Yes";
        
        // ✅ Add unitValues as array
        if (attributeData.unitValues && attributeData.unitValues.trim()) {
          newAttribute.unitValues = attributeData.unitValues.split(',').map(v => v.trim()).filter(v => v);
        } else {
          newAttribute.unitValues = [];
        }
      } else {
        newAttribute.unit = "No";
        newAttribute.unitValues = [];
      }
    } else {
      newAttribute.unit = null;
    }
    
    // ✅ Only add dropdownOptions for DROPDOWN type
    if (attributeData.dataType === "DROPDOWN" && attributeData.dropdownOptions?.length > 0) {
      newAttribute.dropdownOptions = attributeData.dropdownOptions;
    }
    
    const payload = {
      ownerType: "SUBCATEGORY",
      ownerId: parseInt(subCategoryIdValue),
      attributeHeadings: [
        {
          heading: heading.heading,
          productAttributeDtoList: [newAttribute]
        }
      ]
    };

    console.log("📤 Adding New Attribute Payload:", JSON.stringify(payload, null, 2));

    props.getDataFromAPI(
      "/partner/api/v1/attributes",
      "POST",
      payload,
      (response) => {
        setIsSubmitting(false);
        if (onSuccess) onSuccess(attributeData.key);
        toastr.success("Success", `Attribute "${attributeData.key}" added successfully`);
        fetchAttributes();
      },
      (err) => {
        setIsSubmitting(false);
        const errorMsg = err?.message || err?.response?.message || "Failed to add attribute";
        if (onError) onError(errorMsg);
        toastr.error("Error", errorMsg);
      }
    );
  };

  // ✅ Edit attribute - with unitValues support
  const handleEditAttribute = (heading, oldAttribute, newAttributeData, onSuccess, onError) => {
    setIsSubmitting(true);
    
    const attributeId = oldAttribute.id;
    
    if (!attributeId) {
      setIsSubmitting(false);
      if (onError) onError("Attribute ID not found");
      toastr.error("Error", "Attribute ID not found");
      return;
    }
    
    const payload = {
      id: attributeId,
      key: newAttributeData.key,
      dataType: newAttributeData.dataType,
      isRequired: newAttributeData.isRequired === "Yes",
      displayOnPdp: oldAttribute.displayOnPdp || null,
      priority: oldAttribute.priority,
    };
    
    // ✅ Handle Unit for DROPDOWN type
    if (newAttributeData.dataType === "DROPDOWN") {
      if (newAttributeData.unitType === "Yes") {
        payload.unit = "Yes";
        
        // ✅ Add unitValues as array
        if (newAttributeData.unitValues && newAttributeData.unitValues.trim()) {
          payload.unitValues = newAttributeData.unitValues.split(',').map(v => v.trim()).filter(v => v);
        } else {
          payload.unitValues = [];
        }
      } else {
        payload.unit = "No";
        payload.unitValues = [];
      }
    } else {
      payload.unit = null;
    }
    
    // ✅ Add isBoolean for BOOLEAN type
    if (newAttributeData.dataType === "BOOLEAN" && newAttributeData.isBoolean) {
      payload.isBoolean = newAttributeData.isBoolean; // "Yes" or "No"
    }
    
    // ✅ Only add dropdownOptions for DROPDOWN type
    if (newAttributeData.dataType === "DROPDOWN" && newAttributeData.dropdownOptions?.length > 0) {
      payload.dropdownOptions = newAttributeData.dropdownOptions;
    }
    
    console.log("📤 Edit Attribute Payload:", JSON.stringify(payload, null, 2));
    
    props.getDataFromAPI(
      `/partner/api/v1/attributes/${attributeId}`,
      "PUT",
      payload,
      () => {
        setIsSubmitting(false);
        if (onSuccess) onSuccess(newAttributeData.key);
        toastr.success("Success", `Attribute "${newAttributeData.key}" updated successfully`);
        fetchAttributes();
      },
      (err) => {
        setIsSubmitting(false);
        const errorMsg = err?.message || "Failed to update attribute";
        if (onError) onError(errorMsg);
        toastr.error("Error", errorMsg);
        fetchAttributes();
      }
    );
  };

  // ✅ Delete attribute
  const handleDeleteAttribute = (heading, attribute, onSuccess, onError) => {
    setIsSubmitting(true);
    
    const attributeId = attribute.id;
    
    if (!attributeId) {
      setIsSubmitting(false);
      if (onError) onError("Attribute ID not found");
      toastr.error("Error", "Attribute ID not found");
      return;
    }
    
    props.getDataFromAPI(
      `/partner/api/v1/attributes/${attributeId}`,
      "DELETE",
      undefined,
      (response) => {
        setIsSubmitting(false);
        if (onSuccess) {
          onSuccess(attribute.key);
        }
        toastr.success("Success", `Attribute "${attribute.key}" deleted successfully`);
        fetchAttributes();
      },
      (err) => {
        setIsSubmitting(false);
        const errorMsg = err?.message || err?.status?.message || "Failed to delete attribute";
        if (onError) {
          onError(errorMsg);
        }
        toastr.error("Error", errorMsg);
        fetchAttributes();
      }
    );
  };

  // ✅ Edit heading
  const handleEditHeading = (heading, newName, onSuccess, onError) => {
    setIsSubmitting(true);
    
    const oldHeading = heading.heading;
    const ownerId = parseInt(subCategoryIdValue);
    
    const queryParams = new URLSearchParams({
      ownerType: "SUBCATEGORY",
      ownerId: ownerId,
      oldHeading: oldHeading,
      newHeading: newName
    }).toString();
    
    const apiUrl = `/partner/api/v1/attributes/heading?${queryParams}`;
    
    props.getDataFromAPI(
      apiUrl,
      "PUT",
      undefined,
      (response) => {
        setIsSubmitting(false);
        if (onSuccess) onSuccess(newName);
        toastr.success("Success", `Heading updated to "${newName}"`);
        fetchAttributes();
      },
      (err) => {
        setIsSubmitting(false);
        const errorMsg = err?.message || err?.response?.message || "Failed to update heading";
        if (onError) onError(errorMsg);
        toastr.error("Error", errorMsg);
        fetchAttributes();
      }
    );
  };

  // ✅ Drag and drop headings
  const handleDragEndHeadings = (oldIndex, newIndex) => {
    const reorderedHeadings = [...attributeHeadings];
    const [movedItem] = reorderedHeadings.splice(oldIndex, 1);
    reorderedHeadings.splice(newIndex, 0, movedItem);
    
    const payload = {
      ownerType: "SUBCATEGORY",
      ownerId: parseInt(subCategoryIdValue),
      attributeHeadings: reorderedHeadings.map(heading => ({
        heading: heading.heading,
        productAttributeDtoList: heading.productAttributeDtoList || []
      }))
    };

    props.getDataFromAPI(
      "/partner/api/v1/attributes",
      "POST",
      payload,
      () => {
        setAttributeHeadings(reorderedHeadings);
        toastr.success("Success", "Headings reordered successfully");
      },
      (err) => {
        toastr.error("Error", err?.message || "Failed to reorder headings");
        fetchAttributes();
      }
    );
  };

  const handleBack = () => navigate(-1);

  const handleAddHeadingClick = () => {
    navigate(`/home/add-attribute-heading/${subCategoryIdValue}`, {
      state: {
        categoryName: categoryName,
        subCategoryName: subCategoryName,
        ownerType: "SUBCATEGORY",
        ownerId: subCategoryIdValue
      }
    });
  };

  return (
    <SubCategoryAttributesComponent
      attributeHeadings={attributeHeadings}
      categoryName={categoryName}
      subCategoryName={subCategoryName}
      subCategoryId={subCategoryIdValue}
      loading={loading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      isSubmitting={isSubmitting}
      onAddAttribute={handleAddAttribute}
      onEditAttribute={handleEditAttribute}
      onDeleteAttribute={handleDeleteAttribute}
      onEditHeading={handleEditHeading}
      onDragEndHeadings={handleDragEndHeadings}
      onBack={handleBack}
      onAddHeadingClick={handleAddHeadingClick}
      ownerType="SUBCATEGORY"
    />
  );
};

export default connect(null, { getDataFromAPI })(SubCategoryAttributesContainer);